<script setup lang="ts">
import { FormLabel, FormInput } from "../../../base-components/Form";
import LoadingIcon from "../../../base-components/LoadingIcon";
import Button from "../../../base-components/Button";
import Preview from "../../../base-components/Preview";
import { ref, reactive, computed, onMounted } from "vue";
import { useMensajesStore } from "../../../services/mensajes";
import { userUserStore } from "../../../stores/user";
import { LogAcciones, Ticketera } from "@fixeduy/restaurantestypes";
import FormSelect from "../../../base-components/Form/FormSelect.vue";
import { useLogAccionesStore } from "../../../services/logAcciones";
import { getCurrentTime } from "../../../utils/fecha";
const userStore = userUserStore();

const mailUsuario = computed(
  () => userStore.usuario?.mail ?? userStore.user?.email ?? ""
);

/** Solo cuentas @fixed.uy o @atiendo.cloud pueden editar o eliminar ticketeras existentes. */
const puedeEditarOEliminarTicketeras = computed(() => {
  const mail = mailUsuario.value.trim().toLowerCase();
  return mail.endsWith("@fixed.uy") || mail.endsWith("@atiendo.cloud");
});
const mensajesStore = useMensajesStore();
const logAccionesStore = useLogAccionesStore();

const claveTicketera = (t: Ticketera) =>
  `${t.nombre.trim().toLowerCase()}|${t.direccion.trim()}|${t.puerto}`;

const formatoTicketera = (t: Ticketera) =>
  `"${t.nombre}" · sector "${t.idSector}" · ${t.direccion}:${t.puerto}`;

/** Diff detallado entre lista guardada y la nueva (misma identidad = nombre+IP+puerto). */
const construirLineasLogTicketeras = (
  anterior: Ticketera[],
  nuevo: Ticketera[]
): string[] => {
  const lineas: string[] = [];
  const pendientesAnterior = anterior.map(t => ({ ...t }));

  for (const tNuevo of nuevo) {
    const k = claveTicketera(tNuevo);
    const idx = pendientesAnterior.findIndex(t => claveTicketera(t) === k);
    if (idx === -1) {
      lineas.push(`Alta: ${formatoTicketera(tNuevo)}`);
      continue;
    }
    const tOld = pendientesAnterior[idx];
    pendientesAnterior.splice(idx, 1);
    const cambiosCampos: string[] = [];
    if (tOld.nombre !== tNuevo.nombre) {
      cambiosCampos.push(`nombre: "${tOld.nombre}" → "${tNuevo.nombre}"`);
    }
    if (tOld.direccion !== tNuevo.direccion) {
      cambiosCampos.push(`Dirección: ${tOld.direccion} → ${tNuevo.direccion}`);
    }
    if (tOld.tipoConexion !== tNuevo.tipoConexion) {
      cambiosCampos.push(
        `Conexión: ${tOld.tipoConexion} → ${tNuevo.tipoConexion}`
      );
    }
    if (tOld.puerto !== tNuevo.puerto) {
      cambiosCampos.push(`puerto: ${tOld.puerto} → ${tNuevo.puerto}`);
    }
    if (tOld.idSector !== tNuevo.idSector) {
      cambiosCampos.push(`sector: "${tOld.idSector}" → "${tNuevo.idSector}"`);
    }
    if (cambiosCampos.length) {
      lineas.push(
        `Modificación (${formatoTicketera(tOld)}): ${cambiosCampos.join("; ")}`
      );
    }
  }

  for (const tOld of pendientesAnterior) {
    lineas.push(`Baja: ${formatoTicketera(tOld)}`);
  }

  return lineas;
};

const emit = defineEmits<{ (e: "cerrar"): void }>();

const cargando = ref<boolean>(false);

const normalizarTicketeras = (list: Ticketera[]): Ticketera[] =>
  list.map(raw => {
    const direccion = raw.direccion ?? "";

    return {
      nombre: raw.nombre,
      direccion: typeof direccion === "string" ? direccion : "",
      puerto: raw.puerto,
      idSector: raw.idSector ?? "",
      tipoConexion: raw.tipoConexion ?? "wifi"
    };
  });

const aplicarTicketerasAlForm = (list: Ticketera[]) => {
  const next = normalizarTicketeras(list);
  formData.ticketeras.splice(0, formData.ticketeras.length, ...next);
};

const formData = reactive({
  ticketeras: normalizarTicketeras(
    userStore.opcionesGeneralesTicketera.ticketeras
  )
});

onMounted(() => {
  aplicarTicketerasAlForm(userStore.opcionesGeneralesTicketera.ticketeras);
});

const ticketeraForm = reactive<Ticketera>({
  nombre: "",
  direccion: "",
  puerto: 9100,
  idSector: "",
  tipoConexion: "wifi"
});

const sectoresOpciones = computed(() =>
  userStore.opcionesGenerales.sectores.map(s => ({
    value: s,
    label: s
  }))
);

const editIndex = ref<number | null>(null);

const resetTicketeraForm = () => {
  ticketeraForm.nombre = "";
  ticketeraForm.direccion = "";
  ticketeraForm.puerto = 9100;
  ticketeraForm.idSector = "";
  ticketeraForm.tipoConexion = "wifi";
  editIndex.value = null;
};

const validarTicketera = () => {
  if (!ticketeraForm.nombre.trim()) {
    mensajesStore.mostrarToast(
      "Debes ingresar un nombre",
      "",
      mensajesStore.mensajeType.error
    );
    return false;
  }

  if (!ticketeraForm.direccion.trim()) {
    mensajesStore.mostrarToast(
      "Debes ingresar una dirección (IP o dirección de dispositivo)",
      "",
      mensajesStore.mensajeType.error
    );
    return false;
  }

  if (!ticketeraForm.puerto || Number(ticketeraForm.puerto) <= 0) {
    mensajesStore.mostrarToast(
      "Debes ingresar un puerto válido",
      "",
      mensajesStore.mensajeType.error
    );
    return false;
  }

  if (!ticketeraForm.idSector) {
    mensajesStore.mostrarToast(
      "Debes seleccionar un sector",
      "",
      mensajesStore.mensajeType.error
    );
    return false;
  }

  return true;
};

const handlerAgregarOEditarTicketera = () => {
  if (!validarTicketera()) return;

  if (editIndex.value !== null && !puedeEditarOEliminarTicketeras.value) {
    mensajesStore.mostrarToast(
      "No tenés permiso para modificar ticketeras existentes",
      "",
      mensajesStore.mensajeType.error
    );
    return;
  }

  const nuevaTicketera: Ticketera = {
    nombre: ticketeraForm.nombre.trim(),
    direccion: ticketeraForm.direccion.trim(),
    puerto: Number(ticketeraForm.puerto),
    idSector: ticketeraForm.idSector,
    tipoConexion: ticketeraForm.tipoConexion
  };

  if (editIndex.value !== null) {
    formData.ticketeras[editIndex.value] = nuevaTicketera;

    mensajesStore.mostrarToast(
      "Ticketera editada con éxito",
      "",
      mensajesStore.mensajeType.exito
    );
  } else {
    formData.ticketeras.push(nuevaTicketera);

    mensajesStore.mostrarToast(
      "Ticketera agregada con éxito",
      "",
      mensajesStore.mensajeType.exito
    );
  }

  resetTicketeraForm();
};

const handlerEditarTicketera = (index: number) => {
  if (!puedeEditarOEliminarTicketeras.value) {
    mensajesStore.mostrarToast(
      "No tenés permiso para editar ticketeras",
      "",
      mensajesStore.mensajeType.error
    );
    return;
  }

  const ticketera = formData.ticketeras[index];

  ticketeraForm.nombre = ticketera.nombre;
  ticketeraForm.direccion = ticketera.direccion;
  ticketeraForm.puerto = ticketera.puerto;
  ticketeraForm.idSector = ticketera.idSector;
  ticketeraForm.tipoConexion = ticketera.tipoConexion;

  editIndex.value = index;
};

const handlerEliminarTicketera = (index: number) => {
  if (!puedeEditarOEliminarTicketeras.value) {
    mensajesStore.mostrarToast(
      "No tenés permiso para eliminar ticketeras",
      "",
      mensajesStore.mensajeType.error
    );
    return;
  }

  formData.ticketeras.splice(index, 1);

  // Si estaba editando esa misma o una posterior, reseteamos
  if (editIndex.value !== null) {
    if (editIndex.value === index) {
      resetTicketeraForm();
    } else if (editIndex.value > index) {
      editIndex.value = editIndex.value - 1;
    }
  }

  mensajesStore.mostrarToast(
    "Ticketera eliminada con éxito",
    "",
    mensajesStore.mensajeType.exito
  );
};

const handlerGuardar = async () => {
  const anteriorGuardado = normalizarTicketeras(
    userStore.opcionesGeneralesTicketera?.ticketeras ?? []
  );

  try {
    cargando.value = true;

    await userStore.setOpcionesGeneralesTicketera({
      ticketeras: formData.ticketeras
    });

    const detalle = construirLineasLogTicketeras(
      anteriorGuardado,
      formData.ticketeras.map(t => ({ ...t }))
    );

    if (detalle.length) {
      const logAcciones: LogAcciones = {
        id: "",
        accion: [...detalle],
        modulo: "opcionesTicketeras",
        fecha: getCurrentTime(),
        usuario: userStore.userLoggedIn?.uid!,
        mail: userStore.userLoggedIn?.email!
      };
      await logAccionesStore.guardarLog(logAcciones);
    }

    mensajesStore.mostrarToast(
      "Opciones editadas con éxito",
      "",
      mensajesStore.mensajeType.exito
    );

    emit("cerrar");
  } catch (error) {
    console.error("Error al guardar opciones de ticketeras", error);

    mensajesStore.mostrarToast(
      "Error al guardar las opciones",
      "",
      mensajesStore.mensajeType.error
    );
  } finally {
    cargando.value = false;
  }
};
</script>

<template>
  <Preview class="box mt-5">
    <form class="validate-form p-4" @submit.prevent="">
      <div class="border-b border-slate-200/60 dark:border-darkmode-400 pb-6">
        <div
          class="w-full pb-4 cursor-pointer flex-row flex"
          @click="
            () => {
              emit('cerrar');
            }
          "
        >
          <div class="flex w-full justify-center mt-2">
            <h1 class="self-center text-xl mt-2">Opciones ticketeras</h1>
          </div>
        </div>

        <!-- Formulario alta / edición -->
        <div class="grid grid-cols-12 gap-4 mt-4">
          <div class="col-span-12 md:col-span-4">
            <FormLabel htmlFor="ticketera-nombre">Nombre</FormLabel>
            <FormInput
              id="ticketera-nombre"
              v-model="ticketeraForm.nombre"
              type="text"
              placeholder="Ej: Cocina"
            />
          </div>

          <div class="col-span-12 md:col-span-4">
            <FormLabel htmlFor="ticketera-sectores">Sectores</FormLabel>
            <FormSelect
              v-if="sectoresOpciones.length > 0"
              id="ticketera-sectores"
              v-model="ticketeraForm.idSector"
              class="w-full"
              :options="{ placeholder: 'Seleccionar un sector' }"
            >
              <option
                v-for="s in sectoresOpciones"
                :key="s.value"
                :value="s.value"
              >
                {{ s.label }}
              </option>
            </FormSelect>
            <p v-else class="text-slate-500 dark:text-slate-400 text-sm mt-1">
              Configurá sectores en Opciones generales primero.
            </p>
          </div>

          <div class="col-span-12 md:col-span-4">
            <FormLabel htmlFor="ticketera-tipo-conexion">Conexión</FormLabel>
            <FormSelect
              id="ticketera-tipo-conexion"
              v-model="ticketeraForm.tipoConexion"
              class="w-full"
            >
              <option value="wifi">Wi‑Fi (red)</option>
              <option value="bluetooth">Bluetooth</option>
            </FormSelect>
          </div>

          <div class="col-span-12 md:col-span-4">
            <FormLabel htmlFor="ticketera-direccion">Dirección</FormLabel>
            <FormInput
              id="ticketera-direccion"
              v-model="ticketeraForm.direccion"
              type="text"
              placeholder="Ej: 192.168.1.100"
            />
          </div>

          <div class="col-span-12 md:col-span-2">
            <FormLabel htmlFor="ticketera-puerto">Puerto</FormLabel>
            <FormInput
              id="ticketera-puerto"
              v-model="ticketeraForm.puerto"
              type="number"
              placeholder="9100"
            />
          </div>

          <div class="col-span-12 md:col-span-2 flex items-end gap-2">
            <Button
              variant="primary"
              type="button"
              @click="handlerAgregarOEditarTicketera"
            >
              {{ editIndex !== null ? "Actualizar" : "Agregar" }}
            </Button>

            <Button
              v-if="editIndex !== null"
              variant="secondary"
              type="button"
              @click="resetTicketeraForm"
            >
              Cancelar
            </Button>
          </div>
        </div>
      </div>

      <!-- Listado -->
      <div class="mt-6">
        <h2
          class="text-lg font-medium"
          :class="puedeEditarOEliminarTicketeras ? 'mb-4' : 'mb-2'"
        >
          Ticketeras configuradas
        </h2>
        <p
          v-if="!puedeEditarOEliminarTicketeras"
          class="text-slate-500 dark:text-slate-400 text-sm mb-4"
        >
          Con tu cuenta solo podés agregar ticketeras nuevas. Para editar o
          eliminar las ya configuradas, comunicate con soporte.
        </p>

        <div
          v-if="formData.ticketeras.length === 0"
          class="text-slate-500 dark:text-slate-400"
        >
          No hay ticketeras configuradas.
        </div>

        <div v-else class="space-y-3">
          <div
            v-for="(ticketera, index) in formData.ticketeras"
            :key="`${ticketera.nombre}-${index}`"
            class="border rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-slate-200 dark:border-darkmode-400"
          >
            <div>
              <div class="font-medium">{{ ticketera.nombre }}</div>
              <div class="text-slate-600 dark:text-slate-400 text-sm">
                Sector:
                {{ ticketera.idSector }}
                · {{ ticketera.tipoConexion === "wifi" ? "IP" : "BT" }}:
                {{ ticketera.direccion }} | Puerto: {{ ticketera.puerto }}
              </div>
            </div>

            <div class="flex gap-2">
              <Button
                v-if="puedeEditarOEliminarTicketeras"
                variant="outline-primary"
                type="button"
                @click="handlerEditarTicketera(index)"
              >
                Editar
              </Button>

              <Button
                v-if="puedeEditarOEliminarTicketeras"
                variant="outline-danger"
                type="button"
                @click="handlerEliminarTicketera(index)"
              >
                Eliminar
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!cargando" class="flex mt-6">
        <div class="flex-1 text-left mt-3">
          <Button
            variant="secondary"
            class="mb-2 mr-1"
            type="button"
            @click="
              () => {
                emit('cerrar');
              }
            "
          >
            Cancelar
          </Button>
        </div>

        <div class="flex-1 text-right">
          <Button
            variant="primary"
            class="mb-2 mr-1 mt-3"
            type="button"
            @click="handlerGuardar"
          >
            Guardar opciones impresión
          </Button>
        </div>
      </div>

      <div v-else class="flex my-6">
        <div class="mx-auto">
          <LoadingIcon icon="oval" class="w-8 h-8" />
        </div>
      </div>
    </form>
  </Preview>
</template>
